<script setup>
import {
  differenceInDays,
  format,
  getMonth,
  getYear,
  isBefore,
  isSameMonth,
} from "date-fns";
import queryString from "query-string";
import NestedExpand from "~/components/Explore/NestedExpand.vue";
import debounce from "just-debounce-it";


definePageMeta({
  layout: "empty",
  auth: false,
});

const { data: studyAreas } = useFetch("/api/study-area");
const { data: degrees } = useFetch("/api/degree");
const { data: countries } = useFetch("/api/country");
const { data: universityType } = useFetch("/api/uni-type");
const { data: degreeStructures } = useFetch("/api/program-highlight");
const { data: durations } = useFetch("/api/program-duration");
const { data: languageNeeds } = useFetch("/api/language");
const { data: studyModes } = useFetch("/api/study-format");
const { data: schedulePreferences } = useFetch("/api/schedule-preference");

const tuitionFees = ref([
  { label: "0 ~ 100,000 THB", value: "0-100000" },
  { label: "100,000 ~ 300,000 THB", value: "100000-300000" },
  { label: "300,000 ~ 600,000 THB", value: "300000-600000" },
  { label: "Above 600,000 THB", value: "600000" },
]);

const tabs = [
  { label: "All programs", value: "all_programs" },
  { label: "Ranking", value: "ranking" },
  { label: "Scholarships available", value: "scholarships_available" },
  { label: "Tuition fees", value: "tuition_fee" },
];

const filterGroups = [
  // { title: "University Type", key: "universityTypes", options: universityType },
  { title: "Degree", key: "degrees", options: degrees },
  {
    title: "Degree Structure",
    key: "degreeStructures",
    options: degreeStructures,
  },
  { title: "Duration", key: "durations", options: durations },
  { title: "Tuition Fees", key: "tuitionFees", options: tuitionFees },
  { title: "Language Needs", key: "languages", options: languageNeeds },
  { title: "Location", key: "locations", options: countries },
  { title: "Study Modes", key: "studyModes", options: studyModes },
  {
    title: "Schedule Preferences",
    key: "studyPreferences",
    options: schedulePreferences,
  },
];

const route = useRoute();
const router = useRouter();

const op = ref();
const activeTab = ref("all_programs");

const allFilters = ref([]);
const tuitionFeeSortFilter = ref("low_to_high");
const locations = ref([]);
const pageNumber = ref(1);
const pageSize = ref(10);

const isLoading = ref(false);

const selectedCountries = ref([]);
const currentYear = getYear(new Date());
const currentMonth = getMonth(new Date());
const selectedIntakes = ref([]);
const selectedIntakeYear = ref([]);
const mobileDrawerVisible = ref(false);

const months = ref([
  { label: "January", value: "01" },
  { label: "February", value: "02" },
  { label: "March", value: "03" },
  { label: "April", value: "04" },
  { label: "May", value: "05" },
  { label: "June", value: "06" },
  { label: "July", value: "07" },
  { label: "August", value: "08" },
  { label: "September", value: "09" },
  { label: "October", value: "10" },
  { label: "November", value: "11" },
  { label: "December", value: "12" },
]);

watch(selectedIntakeYear, (newVal) => {
  const allMonths = months.value.map((mth) => mth.value);

  if (newVal.length === 2) {
    selectedIntakes.value = allMonths;
  }
  //
  else if (newVal.length === 1) {
    let selectedMonths = allMonths.slice(0, currentMonth);

    if (newVal.at(0) === currentYear) {
      selectedMonths = allMonths.slice(currentMonth);
    }

    selectedIntakes.value = selectedMonths;
  }
  //
  else {
    selectedIntakes.value = [];
  }
});

function getIntakeMonth(month) {
  if (selectedIntakes.value.includes(month)) {
    selectedIntakes.value = selectedIntakes.value.filter((v) => v !== month);
  } else {
    selectedIntakes.value.push(month);
  }
}

const updateQueryParams = () => {
  const studyArea = allFilters.value.filter((f) => f.key === "studyArea").map((f) => f.value);
  const degrees = allFilters.value.filter((f) => f.key === "degrees").map((f) => f.value);
  const degreeStructures = allFilters.value.filter((f) => f.key === "degreeStructures").map((f) => f.value);
  const durations = allFilters.value.filter((f) => f.key === "durations").map((f) => f.value);
  const languages = allFilters.value.filter((f) => f.key === "languages").map((f) => f.value);
  const studyPreferences = allFilters.value.filter((f) => f.key === "studyPreferences").map((f) => f.value);
  const universityTypes = allFilters.value.filter((f) => f.key === "universityTypes").map((f) => f.value);
  const tuitionFees = allFilters.value.filter((f) => f.key === "tuitionFees").map((f) => f.value);
  const locations = allFilters.value.filter((f) => f.key === "locations").map((f) => f.value);
  const keyword = allFilters.value.find((f) => f.key === "keyword")?.value;

  const qStr = queryString.stringify(
    {
      programType: activeTab.value,
      page: pageNumber.value,
      pageSize: pageSize.value,
      tuition_fee_option: activeTab.value === "tuition_fee" ? tuitionFeeSortFilter.value : undefined,
      intakes: selectedIntakes.value,
      studyArea,
      degrees,
      degreeStructures,
      durations,
      languages,
      studyPreferences,
      universityTypes,
      locations,
      countries: selectedCountries.value.map((c) => c.value),
      keyword,
    },
    { arrayFormat: "index" }
  );

  const tuitionFeesQueryStr = tuitionFees.reduce((acc, cur, index) => {
    const [start, end] = cur.split("-");
    acc += `${index > 0 ? "&" : ""}tuitionFees[${index}][start]=${start}${
      end ? `&tuitionFees[${index}][end]=${end}` : ""
    }`;
    return acc;
  }, "");

  const finalQueryStr = qStr.length > 0 ? qStr + "&" + tuitionFeesQueryStr : tuitionFeesQueryStr;

  router.replace({
    query: queryString.parse(finalQueryStr),
  });

  // refetchPrograms();
};

const debouncedUpdateQueryParams = debounce(updateQueryParams, 300);


// Watch for changes in query.keyword and update allFilters
watch(
  () => route.query.keyword,
  (newKeyword) => {
    if (newKeyword) {
      console.log({ newKeyword });
      // Check if keyword filter already exists
      const keywordFilterIndex = allFilters.value.findIndex(
        (filter) => filter.key === "keyword",
      );

      // If keyword filter exists, update it
      if (keywordFilterIndex !== -1) {
        allFilters.value[keywordFilterIndex] = {
          key: "keyword",
          value: newKeyword,
          label: newKeyword,
        };
      }
      // If keyword filter doesn't exist, add it
      else {
        allFilters.value.push({
          key: "keyword",
          value: newKeyword,
          label: newKeyword,
        });
      }
    }
    // If keyword is removed from query, remove it from filters
    else {
      allFilters.value = allFilters.value.filter(
        (filter) => filter.key !== "keyword",
      );
    }
  },
);

// All Filter Query
// Watch for changes in filter values and update URL
watch(
  [allFilters, activeTab, pageNumber, pageSize, tuitionFeeSortFilter, selectedIntakes],
  () => {
    isLoading.value = true;
    debouncedUpdateQueryParams();
    setTimeout(() => {
      isLoading.value = false;
    }, 300);
  },
  { deep: true },
);


// API
const { data: programData, status } = await usePublicFetch(
  () => `programs?published=true&${route.fullPath.split("?")[1] || ""}`,
  {
    key: "explore-programs",
    transform(programs) {
      const modifyPrograms = programs?.data?.map((program) => ({
        id: program?.id,
        slug: program?.slug,
        name: program?.name,

        universityId: program?.university?.id,
        universityName: program?.university?.name,
        universitySlug: program?.university?.slug,

        ranking: getRankingLabel(program?.university?.ranking),

        tuitionFees: program?.tuition_fee?.price,
        tuitionFeeCurrency: program?.tuition_fee?.currency,
        recurringPaymentType: program?.tuition_fee?.recurring_payment_type,

        admissionFees: program?.tuition_fee?.application_fee,
        admissionFeeCurrency: program?.tuition_fee?.currency,

        // upcomingIntake: formatDate(getNearestStartDate(program?.intakes?.data)),
        // deadline: formatDate(getNearestEndDate(program?.intakes?.data)),
        intakes: program?.intakes?.data,

        // location
        location: getCitiesName(program?.university?.cities),

        // attributes
        programHighlight: program?.attributes?.data?.find(
          (att) => att?.key === "program_highlight",
        )?.value,

        studyFormat: program?.attributes?.data?.find(
          (att) => att?.key === "study_format",
        )?.value,

        degree: program?.attributes?.data?.find((att) => att?.key === "degree")
          ?.value,

        language: program?.attributes?.data?.find(
          (att) => att?.key === "language",
        )?.value,

        duration: program?.attributes?.data?.find(
          (att) => att?.key === "duration",
        )?.value,

        schedulePreference: program?.university?.attributes?.data?.find(
          (att) => att?.key === "schedule_preference",
        )?.value,
      }));

      return { modifyPrograms, totalPrograms: programs?.total };
    },
  },
);

const nuxtApp = useNuxtApp();

const loadData = async (event) => {
  const { page } = event;
  pageNumber.value = page + 1;
  document.body.scrollTo({ top: 0, behavior: "smooth" });
};

function updateActiveTab(tab) {
  pageNumber.value = 1;
  activeTab.value = tab;
}

function removeFilterBadge(name) {
  allFilters.value = allFilters.value.filter((val) => val.label !== name);
}

function clearAllFilter() {
  allFilters.value = [];
  // Reset all filters and URL query parameters
  allFilters.value = [];
  selectedCountries.value = [];
  selectedIntakes.value = [];
  selectedIntakeYear.value = [];
  pageNumber.value = 1;

  // Reset URL by removing all query parameters
  router.replace({
    query: {},
  });
}

function studyAreaFilter(item) {
  console.log(item);
  const studyAreaFilters = allFilters.value.map((f) => f.value);

  if (studyAreaFilters.includes(item.value)) {
    allFilters.value = allFilters.value.filter(
      (filter) => filter.value !== item.value,
    );
  } else {
    allFilters.value.push({ ...item, key: "studyArea" });
  }
}

const toggle = (event) => {
  op.value.toggle(event);
};

const selectSort = (sort) => {
  tuitionFeeSortFilter.value = sort;
  op.value.hide();
};

const getNearestStartDate = (intakeDates) => {
  return (
    intakeDates
      ?.map((intake) => {
        const startDate = new Date(intake.start_date);
        const now = new Date();
        // Set year to current year to only compare month and day
        startDate.setFullYear(now.getFullYear());
        // If date has passed this year, check next year
        if (startDate < now) {
          startDate.setFullYear(now.getFullYear() + 1);
        }
        return startDate;
      })
      ?.sort((a, b) => a - b)
      ?.find((date) => date > new Date()) || new Date()
  );
};

const getNearestEndDate = (intakeDates) => {
  return (
    intakeDates
      ?.map((intake) => {
        const endDate = new Date(intake.end_date);
        const now = new Date();
        // Set year to current year to only compare month and day
        endDate.setFullYear(now.getFullYear());
        // If date has passed this year, check next year
        if (endDate < now) {
          endDate.setFullYear(now.getFullYear() + 1);
        }
        return endDate;
      })
      ?.sort((a, b) => a - b)
      ?.find((date) => date > new Date()) || new Date()
  );
};

function formatDate(inputDate, currentDate = new Date()) {
  if (isSameMonth(inputDate, currentDate)) {
    const daysLeft = differenceInDays(inputDate, currentDate);

    if (daysLeft < 0) {
      return format(inputDate, "dd MMM yyyy");
    }

    if (daysLeft === 0) {
      return "Deadline today";
    }

    return `${daysLeft} day(s) left`;
  } else if (isBefore(inputDate, currentDate)) {
    return "Deadline over";
  }

  return format(inputDate, "MMM yyyy");
}

// TODO: update url query string based on search
onMounted(() => {
  // Check for redirect parameter and handle redirection
  const lastVisitedPage = useCookie("lastVisitedPage");
  if (route.query.shouldRedirect === "true" && lastVisitedPage.value) {
    navigateTo(lastVisitedPage.value);
    return;
  }

  // Update filters based on URL query params
  const query = route.query;

  if (query) {
    isLoading.value = true;

    let updatedFilters = [];

    if (query.keyword) {
      updatedFilters.push({
        key: "keyword",
        value: query.keyword,
        label: query.keyword,
      });
    }

    // Set active tab if present
    if (query.programType) {
      activeTab.value = query.programType;
    }

    // Set page number and size
    if (query.page) {
      pageNumber.value = Number(query.page);
    }
    if (query.pageSize) {
      pageSize.value = Number(query.pageSize);
    }

    // Set tuition fee sort option
    if (query.tuition_fee_option) {
      tuitionFeeSortFilter.value = query.tuition_fee_option;
    }

    // Set intakes
    // Handle intakes array-style params
    const intakeParams = Object.keys(query).filter((key) =>
      key.startsWith("intakes["),
    );
    if (intakeParams.length > 0) {
      // Extract intake values from array-style params
      selectedIntakes.value = intakeParams.map((param) => query[param]);
    } else if (query.intakes) {
      // Handle single intake value
      selectedIntakes.value = [query.intakes];
    }

    // Set filters from query params
    const filterKeys = [
      "studyArea",
      "studyModes",
      "degrees",
      "degreeStructures",
      "durations",
      "languages",
      "studyPreferences",
      "universityTypes",
      "locations",
    ];

    filterKeys.forEach((key) => {
      // Handle array-style query params (e.g. universityTypes[0])
      const values = [];
      Object.keys(query).forEach((param) => {
        if (param.startsWith(key + "[")) {
          values.push(query[param]);
        }
      });

      // Special handling for 'other' locations
      if (key === "locations" && values.includes("Other Cities")) {
        // Get all countries except these locations
        const otherCountries =
          countries.value?.filter(
            (country) =>
              ![
                "Thailand, Bangkok",
                "Thailand, Chiang Mai",
                "Thailand, ChaingRai",
              ].includes(country.value),
          ) || [];

        // Add all other countries to filters
        otherCountries.forEach((country) => {
          updatedFilters.push({
            key: "locations",
            value: country.value,
            label: country.label,
          });
        });
      } else if (key === "degreeStructures") {
        // Handle special cases for degree structures
        values.forEach((value) => {
          let label = value;
          if (value === "N/A") {
            label = "Standard Degree";
          }

          updatedFilters.push({
            key: "degreeStructures",
            value: value,
            label: label,
          });
        });
      } else if (query[key] || values.length > 0) {
        const queryValues =
          values.length > 0
            ? values
            : Array.isArray(query[key])
              ? query[key]
              : [query[key]];

        queryValues.forEach((value) => {
          if (!(key === "locations" && value === "Other Cities")) {
            updatedFilters.push({
              key: key,
              value: value,
              label: value,
            });
          }
        });
      }
    });

    // Handle tuition fees filter
    // Check for tuition fees array-style params
    const tuitionFeesParams = Object.keys(query).filter((key) =>
      key.startsWith("tuitionFees["),
    );

    if (tuitionFeesParams.length > 0) {
      // Group tuition fee parameters into fee objects
      const fees = [];
      const feeIndices = new Set();

      tuitionFeesParams.forEach((param) => {
        const matches = param.match(/tuitionFees\[(\d+)\]/);
        if (matches) {
          feeIndices.add(matches[1]);
        }
      });

      Array.from(feeIndices).forEach((index) => {
        const start = query[`tuitionFees[${index}][start]`];
        const end = query[`tuitionFees[${index}][end]`];

        if (start) {
          fees.push({ start, end });
        }
      });

      fees.forEach((fee) => {
        if (fee.start && fee.end) {
          updatedFilters.push({
            key: "tuitionFees",
            value: `${fee.start}-${fee.end}`,
            label: `${Number(fee.start).toLocaleString()} ~ ${Number(
              fee.end,
            ).toLocaleString()} THB`,
          });
        } else if (fee.start) {
          updatedFilters.push({
            key: "tuitionFees",
            value: fee.start,
            label: `Above ${fee.start} THB`,
          });
        }
      });
    }

    allFilters.value = updatedFilters;

    setTimeout(() => {
      isLoading.value = false;
    }, 1000);
  }
});

// --- SEO ---
useHead({
  link: [{ rel: "canonical", href: "https://unilinks.co/explore" }],
});

useSeoMeta({
  title: "Explore Study Abroad & Education in Thailand | UniLinks",
  ogTitle: "Explore Study Abroad & Education in Thailand | UniLinks",
  description:
    "Start your journey to study abroad! Explore education opportunities, Thai universities, transfer programs, and scholarships tailored for international students.",
  ogDescription:
    "Start your journey to study abroad! Explore education opportunities, Thai universities, transfer programs, and scholarships tailored for international students.",
  ogImage: META_IMAGE_URL,
  ogUrl: "https://unilinks.co/explore",
  twitterCard: "summary_large_image",
  twitterTitle: "Explore Study Abroad & Education in Thailand | UniLinks",
  twitterDescription:
    "Start your journey to study abroad! Explore education opportunities, Thai universities, transfer programs, and scholarships tailored for international students.",
  twitterImage: META_IMAGE_URL,
});
</script>

<template>
  <NuxtLayout name="primary">
    <div class="mx-auto w-full lg:w-full max-w-7xl px-4 lg:px-4 mb-12 mt-5">
      <div class="flex flex-nowrap gap-[30px]">
        <!-- Desktop Filters -->
        <section
          class="hidden lg:block w-[260px] px-2 space-y-4 shrink-0 overflow-y-auto"
          :style="{ height: 'calc(100vh - 120px)' }"
        >
          <Collapsible title="Degree" icon="pi-graduation-cap">
            <div
              v-for="degree in degrees"
              :key="degree.value + 'degree-key'"
              class="flex items-center justify-between py-2 gap-2"
            >
              <Checkbox
                :inputId="degree.value"
                :value="{ ...degree, key: 'degrees' }"
                name="degree"
                v-model="allFilters"
              />
              <label
                :for="degree.value"
                class="flex-1 text-sm font-normal text-[#4B5563]"
              >
                {{ degree.label }}
              </label>
            </div>
          </Collapsible>

          <Collapsible title="Study Area" icon="pi-th-large">
            <div
              v-for="(studyArea, index) in studyAreas"
              :key="studyArea.label + 'study-area-key' + index"
              class="flex items-start justify-between py-2 gap-2 cursor-pointer"
              @click="() => studyAreaFilter(studyArea)"
            >
              <i
                :class="[
                  'text-md pi pi-sitemap',
                  allFilters.map((f) => f.value).includes(studyArea.value)
                    ? 'text-blue-500'
                    : '',
                ]"
              ></i>
              <p
                :class="[
                  'flex-1 text-sm font-normal',
                  allFilters.map((f) => f.value).includes(studyArea.value)
                    ? 'text-blue-500'
                    : '',
                ]"
              >
                {{ studyArea.label }}
              </p>
            </div>
          </Collapsible>

          <!-- <Collapsible title="University Type" icon="pi-building">
            <div
              v-for="type in universityType"
              :key="type.value + 'uni-type-key'"
              class="flex items-center justify-between py-2 gap-2"
            >
              <Checkbox
                :inputId="type.value"
                :value="{ ...type, key: 'universityTypes' }"
                name="university-type"
                v-model="allFilters"
              />
              <label
                :for="type.value"
                class="flex-1 text-sm font-normal text-[#4B5563]"
              >
                {{ type.label }}
              </label>
            </div>
          </Collapsible> -->

          <Collapsible title="Degree Structure" icon="pi-graduation-cap">
            <div
              v-for="degreeStr in degreeStructures"
              :key="degreeStr.value + 'degree-structure-key'"
              class="flex items-center justify-between py-2 gap-2"
            >
              <Checkbox
                :inputId="degreeStr.value"
                :value="{ ...degreeStr, key: 'degreeStructures' }"
                name="degree-structure"
                v-model="allFilters"
              />
              <label
                :for="degreeStr.value"
                class="flex-1 text-sm font-normal text-[#4B5563]"
              >
                {{ degreeStr.label }}
              </label>
            </div>
          </Collapsible>

          <Collapsible title="Duration" icon="pi-hourglass">
            <div
              v-for="duration in durations"
              :key="duration.value + 'duration-key'"
              class="flex items-center justify-between py-2 gap-2"
            >
              <Checkbox
                :inputId="duration.value"
                :value="{ ...duration, key: 'durations' }"
                name="duration"
                v-model="allFilters"
              />
              <label
                :for="duration.value"
                class="flex-1 text-sm font-normal text-[#4B5563]"
              >
                {{ duration.label }}
              </label>
            </div>
          </Collapsible>

          <Collapsible title="Intake" icon="pi-calendar">
            <NestedExpand
              v-for="year in currentMonth === 0
                ? [currentYear]
                : [currentYear, currentYear + 1]"
              :key="year + 'intake-key'"
              :value="year + ''"
            >
              <template #default>
                <Checkbox
                  :inputId="year + ''"
                  :value="year"
                  name="intake-checkbox"
                  v-model="selectedIntakeYear"
                />
              </template>

              <template #listItem>
                <li
                  :key="year + month.label + 'intake-year-key'"
                  v-for="month in currentYear === year
                    ? months.slice(currentMonth)
                    : months.slice(0, currentMonth)"
                  @click="() => getIntakeMonth(month.value)"
                  :class="[
                    'p-2 pl-12 text-sm font-normal cursor-pointer bg-[#FAFAFA]',
                    selectedIntakes.includes(month.value)
                      ? 'text-blue-500'
                      : 'text-[#4B5563]',
                  ]"
                >
                  {{ month.label }}
                </li>
              </template>
            </NestedExpand>
          </Collapsible>

          <Collapsible title="Tuition Fees" icon="pi-wallet">
            <div
              v-for="fee in tuitionFees"
              :key="fee.value + 'tuition-fees-key'"
              class="flex items-center justify-between py-2 gap-2"
            >
              <Checkbox
                :inputId="fee.value"
                :value="{
                  key: 'tuitionFees',
                  value: fee.value,
                  label: fee.label,
                }"
                name="tuition-fee"
                v-model="allFilters"
                :checked="
                  allFilters.some(
                    (f) => f.key === 'tuitionFees' && f.value === fee.value,
                  )
                "
              />
              <label
                :for="fee.value"
                class="flex-1 text-sm font-normal text-[#4B5563]"
              >
                {{ fee.label }}
              </label>
            </div>
          </Collapsible>

          <Collapsible title="Language Needs" icon="pi-flag">
            <div
              v-for="language in languageNeeds.filter(
                (lang) => !['Thai', 'Burmese'].includes(lang.value),
              )"
              :key="language.value + 'language-key'"
              class="flex items-center justify-between py-2 gap-2"
            >
              <Checkbox
                :inputId="language.value"
                :value="{ ...language, key: 'languages' }"
                name="language"
                v-model="allFilters"
              />
              <label
                :for="language.value"
                class="flex-1 text-sm font-normal text-[#4B5563]"
              >
                {{ language.label }}
              </label>
            </div>
          </Collapsible>

          <Collapsible title="Location" icon="pi-calendar">
            <div
              v-for="country in countries"
              :key="country.value + 'country-key'"
              class="flex items-center justify-between py-2 gap-2"
            >
              <Checkbox
                :inputId="country.value"
                :value="{ ...country, key: 'locations' }"
                name="location"
                v-model="allFilters"
              />
              <label
                :for="country.value"
                class="flex-1 text-sm font-normal text-[#4B5563]"
              >
                {{ country.label }}
              </label>
            </div>
          </Collapsible>

          <Collapsible title="Study Modes" icon="pi-book">
            <div
              v-for="mode in studyModes"
              :key="mode.value + 'study-modes-key'"
              class="flex items-center justify-between py-2 gap-2"
            >
              <Checkbox
                :inputId="mode.value"
                :value="{ ...mode, key: 'studyModes' }"
                name="study-mode"
                v-model="allFilters"
              />
              <label
                :for="mode.value"
                class="flex-1 text-sm font-normal text-[#4B5563]"
              >
                {{ mode.label }}
              </label>
            </div>
          </Collapsible>

          <Collapsible title="Schedule Preferences" icon="pi-clock">
            <div
              v-for="schedule in schedulePreferences"
              :key="schedule.value + 'schedule-preferences-key'"
              class="flex items-center justify-between py-2 gap-2"
            >
              <Checkbox
                :inputId="schedule.value"
                :value="{ ...schedule, key: 'studyPreferences' }"
                name="schedule-preferences"
                v-model="allFilters"
              />
              <label
                :for="schedule.value"
                class="flex-1 text-sm font-normal text-[#4B5563]"
              >
                {{ schedule.label }}
              </label>
            </div>
          </Collapsible>
        </section>

        <!-- desktop content -->
        <section class="w-full hidden lg:block">
          <h4
            v-if="allFilters.length > 1"
            class="text-xl font-semibold py-2 pr-6 text-slate-700 mb-2"
          >
            {{
              programData?.totalPrograms > 0
                ? `We found ${numberFormatter.format(
                    programData?.totalPrograms || 0,
                  )} programs for you!`
                : "There is no program."
            }}
          </h4>
          <h4
            v-else
            class="text-xl font-semibold py-2 pr-6 text-slate-700 mb-2"
          >
            {{
              `Total ${numberFormatter.format(
                programData?.totalPrograms || 0,
              )} programs`
            }}
          </h4>

          <div
            class="flex flex-wrap items-center gap-2 my-2"
            v-if="allFilters?.length > 0"
          >
            <FIlterBadge
              v-for="filterItem in allFilters"
              :key="filterItem.value + 'program-filter-key'"
              :title="filterItem.label"
              @remove-badge="removeFilterBadge"
            />

            <p
              v-if="allFilters.length > 1"
              class="cursor-pointer text-sm font-bold text-blue-500"
              @click="clearAllFilter"
            >
              Clear all
            </p>
          </div>

          <div class="space-y-6">
            <div class="flex flex-nowrap">
              <div
                v-for="tab in tabs"
                :key="tab.value + 'program-list-key'"
                :class="[
                  'p-4 border-b-2 cursor-pointer',
                  activeTab === tab.value
                    ? 'border-b-blue-500 text-blue-500'
                    : '',
                ]"
                @click="() => updateActiveTab(tab.value)"
              >
                {{ tab.label }}
              </div>

              <div class="border-b-2 flex-1 flex justify-end items-center">
                <div v-if="activeTab === 'tuition_fee'">
                  <div
                    class="flex flex-nowrap items-c enter border rounded-full text-sm font-semibold text-primary-500 py-2 px-3 gap-2.5 cursor-pointer"
                    @click="toggle"
                  >
                    <i class="text-md pi pi-sort-amount-down"></i>
                    <p class="">
                      Sort by:
                      {{
                        tuitionFeeSortFilter === "low_to_high"
                          ? "Low to High"
                          : "High to Low"
                      }}
                    </p>
                    <i class="text-md pi pi-angle-down"></i>
                  </div>

                  <Popover ref="op" :pt="{ content: '-mt-1' }">
                    <ul class="p-1 rounded-sm">
                      <li
                        v-for="item in ['low_to_high', 'high_to_low']"
                        :key="item + 'sort-program-key'"
                        class="py-2.5 px-4 text-sm text-gray-600 cursor-pointer hover:bg-gray-50"
                        @click="() => selectSort(item)"
                      >
                        {{
                          item === "low_to_high"
                            ? "Price: Low to High"
                            : " Price: High to Low"
                        }}
                      </li>
                    </ul>
                  </Popover>
                </div>
              </div>
            </div>

            <!-- Program Card Listing -->
            <div class="space-y-6 h-screen overflow-y-scroll">
              <div
                v-if="status === 'pending'"
                v-for="i in 3"
                :key="i + 'skeleton-program-card-key'"
                class="rounded-lg bg-white border border-slate-200 hover:drop-shadow"
              >
                <div class="grid grid-cols-10 border-b border-slate-200">
                  <div class="col-span-8 p-6">
                    <div class="space-y-2">
                      <Skeleton height="2rem" class="mb-2"></Skeleton>

                      <div
                        class="w-2/3 flex items-center gap-1 text-slate-800 text-sm"
                      >
                        <Skeleton width="30rem" class="mb-2"></Skeleton>

                        <Divider
                          type="solid"
                          layout="vertical"
                          class="before:w-px before:bg-slate-200"
                        />
                        <Skeleton width="15rem" class="mb-2"></Skeleton>

                        <Divider
                          type="solid"
                          layout="vertical"
                          class="before:w-px before:bg-slate-200"
                        />
                        <Skeleton width="15rem" class="mb-2"></Skeleton>
                      </div>

                      <div class="py-4 flex gap-3 w-4/5">
                        <Skeleton
                          v-for="i in 5"
                          :key="i + 'skeleton-program-item-card-key'"
                          height="2rem"
                          borderRadius="16px"
                          class="mb-2"
                        ></Skeleton>
                      </div>

                      <Skeleton height="2rem" class="mb-2"></Skeleton>
                    </div>
                  </div>

                  <div
                    class="w-full col-span-2 space-y-1 flex flex-col items-center justify-center"
                  >
                    <Skeleton
                      width="9rem"
                      height="12rem"
                      class="mb-2"
                    ></Skeleton>
                  </div>
                </div>

                <div class="p-6 bg-white flex justify-between rounded-lg">
                  <Skeleton height="2rem" class="mb-2"></Skeleton>

                  <div class="flex gap-6">
                    <Skeleton height="2rem" class="mb-2"></Skeleton>
                    <Skeleton height="2rem" class="mb-2"></Skeleton>
                  </div>
                </div>
              </div>

              <ProgramCard
                v-else
                v-for="program in programData?.modifyPrograms"
                :id="program.id"
                :slug="program.slug"
                :key="program.id + 'program-card-key'"
                :name="program.name"
                :universityId="program.universityId"
                :universityName="program.universityName"
                :universitySlug="program.universitySlug"
                :location="program.location"
                :duration="program.duration"
                :ranking="program.ranking"
                :tuitionFees="program.tuitionFees"
                :tuitionFeeCurrency="program.tuitionFeeCurrency"
                :recurringPaymentType="program.recurringPaymentType"
                :admissionFees="program.admissionFees"
                :admissionFeeCurrency="program.admissionFeeCurrency"
                :intakes="program.intakes"
                :programHighlight="program.programHighlight"
                :studyFormat="program.studyFormat"
                :degree="program.degree"
                :language="program.language"
                :schedulePreference="program.schedulePreference"
              />
            </div>
          </div>
          <Paginator
            :rows="pageSize"
            :totalRecords="programData?.totalPrograms"
            @page="loadData"
            class="mt-10"
          ></Paginator>
        </section>

        <!-- mobile content -->
        <section class="w-full block lg:hidden">
          <div
            v-if="allFilters.length > 1"
            class="flex items-center justify-between my-2"
          >
            <h4 class="text-lg font-semibold py-2 pr-4 text-slate-700">
              {{
                programData?.totalPrograms > 0
                  ? `${programData?.totalPrograms} programs found!`
                  : "There is no program."
              }}
            </h4>
            <Button
              text
              label="Filter Programs"
              icon="pi pi-sliders-h"
              class="w-fit justify-center text-primary"
              @click="mobileDrawerVisible = true"
            />
          </div>
          <div v-else class="flex items-center justify-between my-2">
            <!-- mobile filter btn -->
            <h4 class="text-lg font-semibold py-2 pr-4 text-slate-700">
              {{ `Total ${programData?.totalPrograms} programs` }}
            </h4>
            <Button
              text
              label="Filter Programs"
              icon="pi pi-sliders-h"
              class="w-fit justify-center text-primary"
              @click="mobileDrawerVisible = true"
            />
          </div>

          <div class="flex flex-wrap items-center gap-2 my-2">
            <FIlterBadge
              v-for="filterItem in allFilters"
              :key="filterItem.value + 'program-filter-key'"
              :title="filterItem.label"
              @remove-badge="removeFilterBadge"
            />

            <p
              v-if="allFilters.length > 1"
              class="cursor-pointer text-sm text-blue-500"
              @click="clearAllFilter"
            >
              Clear all
            </p>
          </div>

          <div class="space-y-4">
            <div class="flex overflow-x-auto w-full">
              <div
                v-for="tab in tabs"
                :key="tab.value + 'program-list-key'"
                :class="[
                  'min-w-max px-4 py-2 text-sm border-b-2 cursor-pointer whitespace-nowrap',
                  activeTab === tab.value
                    ? 'border-b-blue-500 text-blue-500'
                    : '',
                ]"
                @click="() => updateActiveTab(tab.value)"
              >
                {{ tab.label }}
              </div>
            </div>

            <!-- Program Cards -->
            <div class="space-y-4">
              <div
                v-if="status === 'pending'"
                v-for="i in 5"
                :key="i + 'skeleton-program-card-key'"
                class="rounded-lg bg-white border border-slate-200 p-4"
              >
                <Skeleton height="1.5rem" class="mb-2"></Skeleton>
                <div class="flex gap-2 mb-2">
                  <Skeleton width="40%" height="1rem"></Skeleton>
                  <Skeleton width="30%" height="1rem"></Skeleton>
                </div>
                <Skeleton width="100%" height="1rem" class="mb-2"></Skeleton>
              </div>

              <ProgramCard
                v-else
                v-for="program in programData?.modifyPrograms"
                :id="program.id"
                :slug="program.slug"
                :key="program.id + 'program-card-key'"
                :name="program.name"
                :universityId="program.universityId"
                :universityName="program.universityName"
                :universitySlug="program.universitySlug"
                :location="program.location"
                :duration="program.duration"
                :ranking="program.ranking"
                :tuitionFees="program.tuitionFees"
                :tuitionFeeCurrency="program.tuitionFeeCurrency"
                :recurringPaymentType="program.recurringPaymentType"
                :admissionFees="program.admissionFees"
                :admissionFeeCurrency="program.admissionFeeCurrency"
                :intakes="program.intakes"
                :programHighlight="program.programHighlight"
                :studyFormat="program.studyFormat"
                :degree="program.degree"
                :language="program.language"
                :schedulePreference="program.schedulePreference"
              />
            </div>
          </div>
          <Paginator
            class="w-full"
            template="PrevPageLink CurrentPageReport NextPageLink"
            :rows="pageSize"
            :totalRecords="programData?.totalPrograms"
            @page="loadData"
            currentPageReportTemplate="Page {currentPage} of {totalPages}"
            :pt="{
              root: 'mt-6 px-2',
              content: 'flex justify-between items-center',
              prev: 'px-2.5 py-1.5 rounded-md border border-gray-300',
              next: 'px-2.5 py-1.5 rounded-md border border-gray-300',
            }"
          >
            <template #nexticon>
              <i class="pi pi-arrow-right" />
            </template>
            <template #previcon>
              <i class="pi pi-arrow-left" />
            </template>
          </Paginator>
        </section>
      </div>

      <!-- Mobile Filter Drawer -->
      <Drawer
        v-model:visible="mobileDrawerVisible"
        header="Filter Programs"
        position="left"
        class="w-full max-w-sm lg:hidden"
      >
        <div class="space-y-4">
          <Collapsible title="Degree" icon="pi-graduation-cap" :open="true">
            <div
              v-for="degree in degrees"
              :key="degree.value + 'degree-key'"
              class="flex items-center justify-between py-2 gap-2"
            >
              <Checkbox
                :inputId="degree.value"
                :value="{ ...degree, key: 'degrees' }"
                name="degree"
                v-model="allFilters"
              />
              <label
                :for="degree.value"
                class="flex-1 text-sm font-normal text-[#4B5563]"
              >
                {{ degree.label }}
              </label>
            </div>
          </Collapsible>

          <Collapsible title="Study Area" icon="pi-th-large">
            <div
              v-for="(studyArea, index) in studyAreas"
              :key="studyArea.label + 'study-area-key' + index"
              class="flex items-start justify-between py-2 gap-2 cursor-pointer"
              @click="() => studyAreaFilter(studyArea)"
            >
              <i
                :class="[
                  'text-md pi pi-sitemap',
                  allFilters.map((f) => f.value).includes(studyArea.value)
                    ? 'text-blue-500'
                    : '',
                ]"
              ></i>
              <p
                :class="[
                  'flex-1 text-sm font-normal',
                  allFilters.map((f) => f.value).includes(studyArea.value)
                    ? 'text-blue-500'
                    : '',
                ]"
              >
                {{ studyArea.label }}
              </p>
            </div>
          </Collapsible>

          <!-- <Collapsible title="University Type" icon="pi-building">
            <div
              v-for="type in universityType"
              :key="type.value + 'uni-type-key'"
              class="flex items-center justify-between py-2 gap-2"
            >
              <Checkbox
                :inputId="type.value"
                :value="{ ...type, key: 'universityTypes' }"
                name="university-type"
                v-model="allFilters"
              />
              <label
                :for="type.value"
                class="flex-1 text-sm font-normal text-[#4B5563]"
              >
                {{ type.label }}
              </label>
            </div>
          </Collapsible> -->

          <Collapsible
            :open="false"
            title="Degree Structure"
            icon="pi-graduation-cap"
          >
            <div
              v-for="degreeStr in degreeStructures"
              :key="degreeStr.value + 'degree-structure-key'"
              class="flex items-center justify-between py-2 gap-2"
            >
              <Checkbox
                :inputId="degreeStr.value"
                :value="{ ...degreeStr, key: 'degreeStructures' }"
                name="degree-structure"
                v-model="allFilters"
              />
              <label
                :for="degreeStr.value"
                class="flex-1 text-sm font-normal text-[#4B5563]"
              >
                {{ degreeStr.label }} Degree
              </label>
            </div>
          </Collapsible>

          <Collapsible :open="false" title="Duration" icon="pi-hourglass">
            <div
              v-for="duration in durations"
              :key="duration.value + 'duration-key'"
              class="flex items-center justify-between py-2 gap-2"
            >
              <Checkbox
                :inputId="duration.value"
                :value="{ ...duration, key: 'durations' }"
                name="duration"
                v-model="allFilters"
              />
              <label
                :for="duration.value"
                class="flex-1 text-sm font-normal text-[#4B5563]"
              >
                {{ duration.label }}
              </label>
            </div>
          </Collapsible>

          <Collapsible :open="false" title="Intake" icon="pi-calendar">
            <NestedExpand
              v-for="year in currentMonth === 0
                ? [currentYear]
                : [currentYear, currentYear + 1]"
              :key="year + 'intake-key'"
              :value="year + ''"
            >
              <template #default>
                <Checkbox
                  :inputId="year + ''"
                  :value="year"
                  name="intake-checkbox"
                  v-model="selectedIntakeYear"
                />
              </template>
              <template #listItem>
                <li
                  v-for="month in currentYear === year
                    ? months.slice(currentMonth)
                    : months.slice(0, currentMonth)"
                  :key="year + month.label + 'intake-year-key'"
                  @click="() => getIntakeMonth(month.value)"
                  :class="[
                    'p-2 pl-12 text-sm font-normal cursor-pointer bg-[#FAFAFA]',
                    selectedIntakes.includes(month.value)
                      ? 'text-blue-500'
                      : 'text-[#4B5563]',
                  ]"
                >
                  {{ month.label }}
                </li>
              </template>
            </NestedExpand>
          </Collapsible>

          <Collapsible :open="false" title="Tuition Fees" icon="pi-wallet">
            <div
              v-for="fee in tuitionFees"
              :key="fee.value + 'tuition-fees-key'"
              class="flex items-center justify-between py-2 gap-2"
            >
              <Checkbox
                :inputId="fee.value"
                :value="{
                  key: 'tuitionFees',
                  value: fee.value,
                  label: fee.label,
                }"
                name="tuition-fee"
                v-model="allFilters"
                :checked="
                  allFilters.some(
                    (f) => f.key === 'tuitionFees' && f.value === fee.value,
                  )
                "
              />
              <label
                :for="fee.value"
                class="flex-1 text-sm font-normal text-[#4B5563]"
              >
                {{ fee.label }}
              </label>
            </div>
          </Collapsible>

          <Collapsible :open="false" title="Language Needs" icon="pi-flag">
            <div
              v-for="language in languageNeeds.filter(
                (lang) => !['Thai', 'Burmese'].includes(lang.value),
              )"
              :key="language.value + 'language-key'"
              class="flex items-center justify-between py-2 gap-2"
            >
              <Checkbox
                :inputId="language.value"
                :value="{ ...language, key: 'languages' }"
                name="language"
                v-model="allFilters"
              />
              <label
                :for="language.value"
                class="flex-1 text-sm font-normal text-[#4B5563]"
              >
                {{ language.label }}
              </label>
            </div>
          </Collapsible>

          <Collapsible :open="false" title="Location" icon="pi-calendar">
            <div
              v-for="country in countries"
              :key="country.value + 'country-key'"
              class="flex items-center justify-between py-2 gap-2"
            >
              <Checkbox
                :inputId="country.value"
                :value="{ ...country, key: 'locations' }"
                name="location"
                v-model="allFilters"
              />
              <label
                :for="country.value"
                class="flex-1 text-sm font-normal text-[#4B5563]"
              >
                {{ country.label }}
              </label>
            </div>
          </Collapsible>

          <Collapsible :open="false" title="Study Modes" icon="pi-book">
            <div
              v-for="mode in studyModes"
              :key="mode.value + 'study-modes-key'"
              class="flex items-center justify-between py-2 gap-2"
            >
              <Checkbox
                :inputId="mode.value"
                :value="{ ...mode, key: 'studyModes' }"
                name="study-mode"
                v-model="allFilters"
              />
              <label
                :for="mode.value"
                class="flex-1 text-sm font-normal text-[#4B5563]"
              >
                {{ mode.label }}
              </label>
            </div>
          </Collapsible>

          <Collapsible
            :open="false"
            title="Schedule Preferences"
            icon="pi-clock"
          >
            <div
              v-for="schedule in schedulePreferences"
              :key="schedule.value + 'schedule-preferences-key'"
              class="flex items-center justify-between py-2 gap-2"
            >
              <Checkbox
                :inputId="schedule.value"
                :value="{ ...schedule, key: 'studyPreferences' }"
                name="schedule-preferences"
                v-model="allFilters"
              />
              <label
                :for="schedule.value"
                class="flex-1 text-sm font-normal text-[#4B5563]"
              >
                {{ schedule.label }}
              </label>
            </div>
          </Collapsible>

          <div class="mt-4 text-right">
            <Button
              label="Apply Filters"
              icon="pi pi-check"
              class="w-full justify-center bg-primary-500 border-primary-500 hover:bg-primary-600"
              @click="mobileDrawerVisible = false"
            />
          </div>
        </div>
      </Drawer>
    </div>

    <Footer />
  </NuxtLayout>
</template>
